import {
  Alert,
  Avatar,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  MenuItem,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Container, Stack } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { useFormik } from 'formik';
import moment from 'moment';
import { ordinal } from 'src/utils/progressNoteDue';
import { useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { useSettings } from 'src/hooks/use-settings';
import {
  createPatientPayerAction,
  resetCreatePatientPayerAction,
  resetUpdatePatientPayerAction,
  updatePatientPayerAction,
  validateMdsPayerAction,
  resetValidateMdsPayerAction,
  getPayerDateRangesAction,
  resetGetPayerDateRangesAction,
} from 'src/redux/actions/patientPayerActions';
import { getInitials } from 'src/utils/get-initials';
import * as Yup from 'yup';
import SimpleSnackbar from './sub-components/snackBar';
import { patientsOptionTestId, payerFormTestId } from 'src/sections/dashboard/patients/test-ids';
import WarningModal from 'src/components/therapyPlanning_2.0/commonComponent/warning-modal';
import { addDays } from 'date-fns';
import { parseDateWithoutTimezone } from 'src/utils/convertDateToSelectedTimeZone';
import {
  isNewPayerStartBlocked,
  newPayerMaxEndDate,
  type PayerDateRange,
} from 'src/utils/payerDateBounds';
import { getEnterprisePayerPlanType } from 'src/redux/actions/patientAction';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { docsDueAnchoredModeTreatment } from 'src/config';
import { getFacilityTodayYmd } from 'src/utils/convertDateToSelectedTimeZone';
import {
  backdatedPayerNoteText,
  endCurrentPayerDocsDueSentence,
  shouldShowBackdatedPayerNote,
} from 'src/utils/backdatedPayerDocsDueNote';

// BOPS-419: Darren's format for the help text -- "August 3rd, 2026", not the Start Date field's "Oct 17, 2026".
export const prettyDate = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  const m = moment(date);
  if (!m.isValid()) return null;
  return `${m.format('MMMM')} ${ordinal(m.date())}, ${m.format('YYYY')}`;
};

// BOPS-419: a new payer has no start date yet, so the "before <date>" clause waits until one is picked.
export const priorMedicareADaysHelpText = (startDate: Date | null | undefined): string => {
  const on = prettyDate(startDate);
  const period = on
    ? `in the current 100-day benefit period, before ${on}.`
    : 'in the current 100-day benefit period.';
  return `Medicare A days this resident already used ${period} Count days at this facility and at other facilities. Enter 0 if none.`;
};

// THIN-8148: prior Medicare A days apply to Part A payers only. Tested by the two keys, not by the
// `partA` group in utils-method, which leaves MANAGED_CARE_A under "other".
const isPartAPayerType = (payerType?: string) =>
  payerType === 'MEDICARE_A' || payerType === 'MANAGED_CARE_A';

function getCurrentDateUTCWithoutTime(date: any) {
  // Get the date components
  const utcDateFormate = moment(date).format('YYYY-MM-DD');
  // Construct the UTC date string
  const utcDateString = `${utcDateFormate}`;
  return utcDateString;
}

const programTypes = [
  { key: 'WELLNESS', displayName: 'Wellness' },
  { key: 'RESTORATIVE', displayName: 'Restorative' },
  { key: 'MAINTENANCE', displayName: 'Maintenance' },
];

const AddEditPatientPayer = () => {
  const settings = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { state } = location;
  const action = state?.action ? state?.action : 'ADD';
  const currentPayer = state?.payerInfo ? state?.payerInfo : '';
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getPayerPlanTypeResponse = useSelector(
    (state: any) => state.getPayerPlanTypeReducer?.enterprisePayerPlanType?.data
  );
  const episodeOfCareData = useSelector((state: any) => state?.getSingleEpisodeOfCare?.response);
  const createPatientPayerResponse = useSelector((state: any) => state?.createPatientPayerReducer);
  const updatePatientPayerResponse = useSelector((state: any) => state?.updatePatientPayerReducer);
  const [popUpMessage, setPopUpMessage] = useState({
    message: '',
    type: '',
  });
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [payerWarningInfo, setPayerWarningInfo] = useState({
    title: '',
    message: '',
    cancelBtn: '',
    confirmBtn: '',
  });
  const [forcePayerEnd, setForcePayerEnd] = useState(false);
  const [forceCreatePayerAndEndPreviousPayer, setForceCreatePayerAndEndPreviousPayer] =
    useState(false);
  const [isWarningForAlertsModalOpen, setIsWarningForAlertsModalOpen] = useState(false);
  const [isEndDateUnlocked, setIsEndDateUnlocked] = useState(false);
  const [isUnlockConfirmationOpen, setIsUnlockConfirmationOpen] = useState(false);
  // THIN-8399: set once a save succeeds, so the second before navigate(-1) cannot submit again
  const [isSaved, setIsSaved] = useState(false);
  // THIN-8288: null until Split is ready, which keeps the anchored-mode note and copy off
  const [isDocsDueAnchoredMode] = useFeatureToggleWhenReady(
    docsDueAnchoredModeTreatment,
    'FACILITY'
  );

  const getAllPatientPayersReponse = useSelector(
    (state: any) => state?.getAllPatientPayersReducer?.response?.data
  );

  const shouldWarnAboutPastAlerts = useMemo(
    () =>
      getAllPatientPayersReponse?.content?.filter((i: any) => i.uuid == currentPayer?.uuid)[0]
        ?.shouldWarnAboutPastAlerts || false,
    [currentPayer, getAllPatientPayersReponse]
  );

  // Backend-driven MDS payer validation
  const validateMdsPayerResponse = useSelector((state: any) => state?.validateMdsPayerReducer);
  const [isMdsPayerWarningOpen, setIsMdsPayerWarningOpen] = useState(false);
  const [mdsPayerWarningMessage, setMdsPayerWarningMessage] = useState('');

  // THIN-8399: any payer request in flight blocks another submit
  const isSaving = Boolean(
    createPatientPayerResponse?.loading ||
      updatePatientPayerResponse?.loading ||
      validateMdsPayerResponse?.loading
  );

  // Handle validate-mds-payer API response
  useEffect(() => {
    if (!validateMdsPayerResponse?.response) return;

    const data = validateMdsPayerResponse?.response?.data;
    if (data?.hasWarning) {
      setMdsPayerWarningMessage(data?.warningMessage);
      setIsMdsPayerWarningOpen(true);
    } else {
      // No warning — proceed with actual form submit
      formik.handleSubmit();
    }
    dispatch(resetValidateMdsPayerAction());
  }, [validateMdsPayerResponse?.response]);

  useEffect(() => {
    dispatch(getEnterprisePayerPlanType());
  }, []);

  // THIN-8401: a new payer has no allowable range on it, so bound its pickers by every existing payer instead
  const payerDateRanges: PayerDateRange[] = useSelector(
    (state: any) => state?.getPayerDateRangesReducer?.response?.data ?? []
  );

  useEffect(() => {
    if (action !== 'ADD' || !getPatientId) return;
    dispatch(getPayerDateRangesAction({ patientUuid: getPatientId, programType: 'THERAPY' }));
    return () => {
      dispatch(resetGetPayerDateRangesAction());
    };
  }, [action, getPatientId]);

  useEffect(() => {
    if (createPatientPayerResponse?.response === null && !createPatientPayerResponse?.isSuccess)
      return;
    if (createPatientPayerResponse?.response !== null && !createPatientPayerResponse?.isSuccess) {
      if (
        createPatientPayerResponse?.response?.data?.warnings &&
        !!Object.keys(createPatientPayerResponse?.response?.data?.warnings)?.length
      ) {
        const warnings = createPatientPayerResponse?.response?.data?.warnings;
        if (Object.keys(warnings)[0] === 'ACTIVE_CERT_WARNING_MESSAGE') {
          setPayerWarningInfo({
            title: 'Uncovered Certification Dates',
            message:
              "Updating the payer's dates will leave the certification period without coverage. Please adjust the payer dates and verify that all certification periods are covered to prevent documentation and billing issues.",
            cancelBtn: 'Cancel',
            confirmBtn: 'Confirm',
          });
          setIsWarningModalOpen(true);
          setForcePayerEnd(true);
        } else if (Object.keys(warnings)[0] === 'ACTIVE_DSN_WARNING_MESSAGE') {
          setPayerWarningInfo({
            title: 'Uncovered DSN Detected',
            message:
              'Therapy notes were entered for dates not covered by the selected payer. Please verify that all DSNs fall within payer coverage dates to avoid coverage gaps and billing errors.',
            cancelBtn: 'Cancel',
            confirmBtn: 'Confirm',
          });
          setIsWarningModalOpen(true);
          setForcePayerEnd(true);
        } else if (Object.keys(warnings)[0] === 'END_CURRENT_PAYER_WARNING_MESSAGE') {
          setPayerWarningInfo({
            title: 'Changing a Payer?',
            message: `The start date you selected overlaps with the current payer’s coverage period.\n\nWe can automatically end the current payer one day earlier and begin the new one on your selected date. ${endCurrentPayerDocsDueSentence(
              isDocsDueAnchoredMode
            )} Would you like us to make this update?`,
            cancelBtn: 'Cancel',
            confirmBtn: 'Confirm Payer Update',
          });
          setIsWarningModalOpen(true);
          setForceCreatePayerAndEndPreviousPayer(true);
        }
      } else {
        setPopUpMessage({
          message:
            createPatientPayerResponse?.response?.error ||
            createPatientPayerResponse?.response?.message,
          type: 'ERROR',
        });
        // THIN-8399: a forced create is done once it answers; the next Save must warn again, not force
        setForceCreatePayerAndEndPreviousPayer(false);

        setTimeout(() => {
          setPopUpMessage({
            message: '',
            type: '',
          });
        }, 3000);
      }
    } else {
      setIsSaved(true);
      setForceCreatePayerAndEndPreviousPayer(false);
      setPopUpMessage({
        message: createPatientPayerResponse?.response?.message,
        type: '',
      });
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    }

    // THIN-8399: reset now that this response is handled. This used to run on a 2s timer, which could
    // land while the *next* (forced) create was still in flight - clearing `loading` re-enabled Save and
    // a second click created a duplicate payer.
    dispatch(resetCreatePatientPayerAction());
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 2000);
  }, [createPatientPayerResponse]);

  const resubmitEditPayerHandler = () => {
    if (isSaving || isSaved) return;
    setIsWarningModalOpen(false);

    formik.handleSubmit();
  };

  useEffect(() => {
    if (updatePatientPayerResponse?.response === null && !updatePatientPayerResponse?.isSuccess)
      return;
    if (updatePatientPayerResponse?.response !== null && !updatePatientPayerResponse?.isSuccess) {
      if (
        updatePatientPayerResponse?.response?.data?.warnings &&
        !!Object.keys(updatePatientPayerResponse?.response?.data?.warnings)?.length
      ) {
        const warnings = updatePatientPayerResponse?.response?.data?.warnings;
        if (Object.keys(warnings)[0] === 'ACTIVE_CERT_WARNING_MESSAGE') {
          setPayerWarningInfo({
            title: 'Uncovered Certification Dates',
            message:
              "Updating the payer's dates will leave the certification period without coverage. Please adjust the payer dates and verify that all certification periods are covered to prevent documentation and billing issues.",
            cancelBtn: 'Cancel',
            confirmBtn: 'Confirm',
          });
          setIsWarningModalOpen(true);
          setForcePayerEnd(true);
        } else if (Object.keys(warnings)[0] === 'ACTIVE_DSN_WARNING_MESSAGE') {
          setPayerWarningInfo({
            title: 'Uncovered DSN Detected',
            message:
              'Therapy notes were entered for dates not covered by the selected payer. Please verify that all DSNs fall within payer coverage dates to avoid coverage gaps and billing errors.',
            cancelBtn: 'Cancel',
            confirmBtn: 'Confirm',
          });
          setIsWarningModalOpen(true);
          setForcePayerEnd(true);
        }
      } else {
        setPopUpMessage({
          message:
            updatePatientPayerResponse?.response?.error ||
            updatePatientPayerResponse?.response?.message,
          type: 'ERROR',
        });
        setIsWarningModalOpen(false);
        setForcePayerEnd(false);
        setTimeout(() => {
          setPopUpMessage({
            message: '',
            type: '',
          });
        }, 3000);
      }
    } else {
      setIsSaved(true);
      setForcePayerEnd(false);
      setPopUpMessage({
        message: updatePatientPayerResponse?.response?.message,
        type: '',
      });
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    }

    // THIN-8399: reset immediately, never on a timer that can outlive the next request (see the create effect)
    dispatch(resetUpdatePatientPayerAction());
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 2000);
  }, [updatePatientPayerResponse]);

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues: {
      // programType: action === 'EDIT' ? currentPayer?.programType : '',
      payerType: action === 'EDIT' ? currentPayer?.payerType : '',
      payerPlanEntity: action === 'EDIT' ? currentPayer?.payerPlanId : '',
      startDate: currentPayer?.startDate
        ? new Date(
            new Date(currentPayer?.startDate).valueOf() +
              new Date(currentPayer?.startDate).getTimezoneOffset() * 60 * 1000
          )
        : null,
      endDate: currentPayer?.endDate
        ? new Date(
            new Date(currentPayer?.endDate).valueOf() +
              new Date(currentPayer?.endDate).getTimezoneOffset() * 60 * 1000
          )
        : null,
      priorMedicareADays:
        action === 'EDIT' && currentPayer?.priorMedicareADays != null
          ? currentPayer?.priorMedicareADays
          : '',
    },
    validationSchema: Yup.object({
      // programType:
      //   state?.programType === 'SPECIAL'
      //     ? Yup.string().max(255).required('Program type is required')
      //     : Yup.string().nullable(),
      payerType: Yup.string().max(255).required('Payer type is required'),
      payerPlanEntity: Yup.string().max(255).required('Payer name is required'),
      startDate: Yup.date()
        .required('Start date is required')
        .test(
          'eoc-start-date',
          'Episode Start Date must be after the patient’s Date of Birth',
          function (value) {
            if (!value) return false;
            const selectedEoc = moment(value);
            const formattedDoB = moment(patientData?.dateOfBirth);
            return selectedEoc.isAfter(formattedDoB);
          }
        ),
      endDate: Yup.date()
        .nullable()
        .test('eoc-end-date', 'Payer end date cannot be before the start date.', function (value) {
          if (!value) return true;
          const selectedEndDate = moment(value);
          const startDate = moment(this.parent.startDate);
          return selectedEndDate.isSameOrAfter(startDate);
        }),
      priorMedicareADays: Yup.number()
        .typeError('Prior Medicare A days used must be a number')
        .integer('Prior Medicare A days used must be a whole number')
        .min(0, 'Prior Medicare A days used cannot be negative'),
    }),

    onSubmit: (values) => {
      const startDate = getCurrentDateUTCWithoutTime(values?.startDate || new Date());
      const endDate = values?.endDate ? getCurrentDateUTCWithoutTime(values?.endDate) : null;
      if (action === 'ADD') {
        dispatch(
          createPatientPayerAction({
            patientId: getPatientId,
            startDate: startDate,
            endDate: endDate,
            payerPlanId: values.payerPlanEntity,
            programType: 'THERAPY',
            forceCreatePayerAndEndPreviousPayer: forceCreatePayerAndEndPreviousPayer,
            priorMedicareADays: isPartAPayerType(values.payerType)
              ? Number(values.priorMedicareADays) || 0
              : undefined,
          })
        );
      } else
        dispatch(
          updatePatientPayerAction({
            patientId: getPatientId,
            payerPlanId: values.payerPlanEntity,
            payerUuid: currentPayer?.uuid,
            startDate: startDate,
            endDate: endDate,
            programType: 'THERAPY',
            forceEndPayer: forcePayerEnd,
            priorMedicareADays: isPartAPayerType(values.payerType)
              ? Number(values.priorMedicareADays) || 0
              : undefined,
          })
        );
    },
  });

  const facilityTodayYmd = getFacilityTodayYmd();
  const showBackdatedPayerNote = shouldShowBackdatedPayerNote({
    isAnchoredMode: isDocsDueAnchoredMode,
    action,
    startDate: formik.values.startDate,
    initialStartDate: formik.initialValues.startDate,
    payerPlanId: formik.values.payerPlanEntity,
    initialPayerPlanId: formik.initialValues.payerPlanEntity,
    todayYmd: facilityTodayYmd,
  });

  const getFilterPayerPlans: any = (selectPayerType: any) => {
    const resultList = patientData?.facilityEntity?.payerPlans?.filter(
      (item: any) => item?.payerPlanType === selectPayerType
    );
    return resultList;
  };

  const handlePayerTypeChange = (e: React.ChangeEvent<any>) => {
    formik.setFieldValue('payerType', e.target?.value);
    formik.setFieldValue('payerPlanEntity', '');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  // Call backend validate-mds-payer API before submitting
  const checkMdsPayerValidationAndSubmit = () => {
    const values = formik.values;
    const startDate = getCurrentDateUTCWithoutTime(values?.startDate || new Date());
    const endDate = values?.endDate ? getCurrentDateUTCWithoutTime(values?.endDate) : null;

    dispatch(
      validateMdsPayerAction({
        patientId: getPatientId,
        payerPlanId: values.payerPlanEntity,
        startDate: startDate,
        endDate: endDate,
        payerUuid: action === 'EDIT' ? currentPayer?.uuid : null,
      })
    );
  };

  const handleSubmitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isSaving || isSaved) return;
    if (shouldWarnAboutPastAlerts) {
      setIsWarningForAlertsModalOpen(true);
      return;
    }
    checkMdsPayerValidationAndSubmit();
  };

  const handleStartDateWarningConfirm = () => {
    setIsWarningForAlertsModalOpen(false);
    checkMdsPayerValidationAndSubmit();
  };

  const handleUnlockConfirmation = () => {
    setIsUnlockConfirmationOpen(false);
    setIsEndDateUnlocked(true);
  };

  return (
    <>
      <Seo title="Restore-Insights: Patient" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
        }}
      >
        <Container maxWidth={settings.stretch ? false : 'xl'}>
          <Stack
            spacing={{
              xs: 2,
              lg: 3,
            }}
          >
            <Stack>
              <Link
                color="text.primary"
                component={RouterLink}
                sx={{
                  alignItems: 'center',
                  display: 'inline-flex',
                }}
                underline="hover"
                onClick={handleBackClick}
              >
                <SvgIcon>
                  <ArrowLeftIcon />
                </SvgIcon>
                <Typography variant="subtitle2">
                  {state?.from === 'payers'
                    ? 'Back to Payers List'
                    : state?.from === 'EOC'
                    ? 'Back to Episode Of Care List'
                    : ''}
                </Typography>
              </Link>
            </Stack>

            <Typography variant="h4">
              {state?.action === 'ADD' ? 'Add New Payer' : 'Edit Current Payer'}
            </Typography>
            <Divider />
            <Card sx={{ padding: '1rem', width: '100%' }}>
              <Stack
                alignItems="flex-start"
                direction="row"
                spacing={2}
              >
                <Avatar
                  src={`data:image/png;base64,${patientData?.profileImage}`}
                  sx={{
                    height: 64,
                    width: 64,
                  }}
                >
                  {getInitials(patientData?.lastName) + getInitials(patientData?.firstName)}
                </Avatar>
                <Stack spacing={1}>
                  <Typography variant="h5">
                    {patientData?.lastName + ' ' + patientData?.firstName}
                  </Typography>
                  <Stack
                    alignItems="center"
                    direction="row"
                    spacing={1}
                  >
                    <Chip
                      label={patientData?.active === true ? 'Active' : 'Inactive'}
                      size="small"
                      color={patientData?.active === true ? 'success' : 'warning'}
                      clickable={true}
                    />
                  </Stack>
                </Stack>
              </Stack>

              <Stack mt={4}>
                <form
                  style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}
                  onSubmit={formik.handleSubmit}
                >
                  <Grid
                    container
                    justifyItems={'flex-start'}
                    spacing={3}
                  >
                    {/* {state?.programType === 'SPECIAL' && (
                      <Grid
                        item
                        xs={4}
                      >
                        <TextField
                          fullWidth
                          label="Program Type"
                          error={!!(formik.touched.programType && formik.errors.programType)}
                          helperText={
                            formik.touched.programType && (formik.errors.programType as any)
                          }
                          name="programType"
                          onBlur={formik.handleBlur}
                          onChange={(e) => {
                            const trimmedValue = e.target.value;

                            formik.setFieldValue('programType', trimmedValue);
                          }}
                          value={formik.values.programType}
                          select
                        >
                          {programTypes?.map((option: any) => (
                            <MenuItem
                              key={option.key}
                              value={option.key}
                            >
                              {option.displayName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )} */}

                    <Grid
                      item
                      xs={6}
                    >
                      <TextField
                        fullWidth
                        label="Payer Type"
                        error={!!(formik.touched.payerType && formik.errors.payerType)}
                        helperText={formik.touched.payerType && (formik.errors.payerType as any)}
                        name="payerType"
                        onBlur={formik.handleBlur}
                        // onChange={formik.handleChange}
                        onChange={handlePayerTypeChange}
                        value={formik.values.payerType}
                        select
                        SelectProps={{
                          SelectDisplayProps: {
                            'data-testid': payerFormTestId('payer-type'),
                          } as HTMLAttributes<HTMLDivElement>,
                        }}
                        disabled={action === 'EDIT' && !currentPayer?.canAssignPayerType}
                      >
                        {getPayerPlanTypeResponse?.map((option: any) => (
                          <MenuItem
                            key={option.key}
                            value={option.key}
                            data-testid={patientsOptionTestId(
                              payerFormTestId('payer-type'),
                              option.key
                            )}
                          >
                            {option.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                    >
                      <TextField
                        fullWidth
                        label="Payer name"
                        error={!!(formik.touched.payerPlanEntity && formik.errors.payerPlanEntity)}
                        helperText={
                          formik.touched.payerPlanEntity && (formik.errors.payerPlanEntity as any)
                        }
                        disabled={!formik.values.payerType}
                        name="payerPlanEntity"
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldValue('payerPlanEntity', e.target.value);
                        }}
                        value={formik.values.payerPlanEntity}
                        InputLabelProps={{
                          shrink:
                            formik.values.payerPlanEntity !== '' && !formik.touched.payerPlanEntity
                              ? true
                              : undefined,
                        }}
                        select
                        SelectProps={{
                          SelectDisplayProps: {
                            'data-testid': payerFormTestId('payer-name'),
                          } as HTMLAttributes<HTMLDivElement>,
                        }}
                      >
                        {getFilterPayerPlans(formik.values.payerType)?.length === 0 ? (
                          <Typography
                            color="text.secondary"
                            p={1}
                          >
                            No payer plan found for {formik.values.payerType}
                          </Typography>
                        ) : (
                          getFilterPayerPlans(formik.values.payerType)
                            ?.filter((item: any) => {
                              if (item.active) {
                                return item;
                              } else if (
                                item.payerPlanName ===
                                episodeOfCareData?.payerPlanEntity?.payerPlanName
                              ) {
                                return item;
                              }
                            })
                            ?.map((payer: any) => (
                              <MenuItem
                                key={payer?.id}
                                value={payer?.uuid}
                                disabled={!payer.active}
                                data-testid={patientsOptionTestId(
                                  payerFormTestId('payer-name'),
                                  payer?.uuid
                                )}
                              >
                                {payer?.payerPlanName} {!payer.active ? ' - Inactive' : ''}
                              </MenuItem>
                            ))
                        )}
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                    >
                      <DatePicker
                        disabled={action === 'EDIT' && !currentPayer}
                        minDate={parseDateWithoutTimezone(
                          currentPayer?.minAllowableStartDate?.toString()
                        )}
                        shouldDisableDate={
                          action === 'ADD'
                            ? (date) => isNewPayerStartBlocked(date, payerDateRanges)
                            : undefined
                        }
                        onChange={(newDate) => {
                          formik.setFieldValue('startDate', newDate);
                          if (action === 'EDIT')
                            formik.setFieldValue('payerPlanStartDate', newDate);
                        }}
                        label="Start Date"
                        value={formik.values.startDate}
                        slotProps={{
                          textField: {
                            inputProps: { 'data-testid': payerFormTestId('start-date') },
                          },
                        }}
                        format="MMM dd, yyyy"
                        sx={{
                          width: '100%',
                          border:
                            formik.touched.startDate && formik.errors.startDate
                              ? '2px solid red'
                              : '',
                          borderRadius:
                            formik.touched.startDate && formik.errors.startDate ? '10px' : '',
                        }}
                      />
                      {formik.touched.startDate && formik.errors.startDate && (
                        <Typography
                          padding={'0px 16px'}
                          color={'red'}
                          variant="caption"
                        >
                          {formik.errors.startDate}
                        </Typography>
                      )}
                      {showBackdatedPayerNote && (
                        <Alert
                          severity="info"
                          sx={{ mt: 1 }}
                          data-testid={payerFormTestId('backdated-docs-due-note')}
                        >
                          {backdatedPayerNoteText(facilityTodayYmd)}
                        </Alert>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={6}
                    >
                      <DatePicker
                        disabled={
                          (action === 'EDIT' && !currentPayer) ||
                          (action === 'EDIT' &&
                            currentPayer?.endDate === null &&
                            currentPayer?.isCurrent === true &&
                            !isEndDateUnlocked)
                        }
                        onChange={(newDate) => {
                          formik.setFieldValue('endDate', newDate);
                          if (action === 'EDIT') formik.setFieldValue('payerPlanEndDate', newDate);
                        }}
                        label="End Date"
                        value={formik.values.endDate}
                        minDate={formik.values.startDate}
                        maxDate={parseDateWithoutTimezone(
                          action === 'ADD'
                            ? newPayerMaxEndDate(formik.values.startDate, payerDateRanges)
                            : currentPayer?.maxAllowableEndDate?.toString()
                        )}
                        format="MMM dd, yyyy"
                        sx={{
                          width: '100%',
                          border:
                            formik.touched.endDate && formik.errors.endDate ? '2px solid red' : '',
                          borderRadius:
                            formik.touched.endDate && formik.errors.endDate ? '10px' : '',
                        }}
                      />
                      {formik.touched.endDate && formik.errors.endDate && (
                        <Typography
                          padding={'0px 16px'}
                          color={'red'}
                          variant="caption"
                        >
                          {formik.errors.endDate}
                        </Typography>
                      )}
                      {formik.values.endDate &&
                        !(
                          action === 'EDIT' &&
                          currentPayer?.endDate === null &&
                          currentPayer?.isCurrent === true &&
                          !isEndDateUnlocked
                        ) && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => formik.setFieldValue('endDate', null)}
                            sx={{ mt: 0.5, px: '8px', textTransform: 'none' }}
                          >
                            Clear End Date
                          </Button>
                        )}
                      {action === 'EDIT' &&
                        currentPayer?.endDate === null &&
                        currentPayer?.isCurrent === true &&
                        !isEndDateUnlocked && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mt: 1,
                              px: '8px',
                            }}
                          >
                            <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', gap: 1 }}>
                              <LockIcon
                                sx={{
                                  fontSize: '20px',
                                  color: '#111927',
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: '#111927' }}
                                component="span"
                              >
                                End date will be automatically set based on the start date of the
                                next payer to ensure coverage continuity{' '}
                                <Link
                                  component="button"
                                  type="button"
                                  onClick={() => setIsUnlockConfirmationOpen(true)}
                                  sx={{
                                    color: '#3a3ee7',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  Unlock to Edit Manually
                                </Link>
                              </Typography>
                            </Box>
                          </Box>
                        )}
                    </Grid>
                    {isPartAPayerType(formik.values.payerType) && (
                      <Grid
                        item
                        xs={6}
                      >
                        <TextField
                          fullWidth
                          type="number"
                          label="Prior Medicare A days used in the current 100-day benefit period"
                          name="priorMedicareADays"
                          inputProps={{ min: 0, step: 1 }}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.priorMedicareADays}
                          error={
                            !!(
                              formik.touched.priorMedicareADays && formik.errors.priorMedicareADays
                            )
                          }
                          helperText={
                            formik.touched.priorMedicareADays &&
                            (formik.errors.priorMedicareADays as any)
                          }
                          data-testid={payerFormTestId('prior-medicare-a-days')}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {priorMedicareADaysHelpText(formik.values.startDate)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  <Stack
                    flexDirection="row"
                    justifyContent="flex-end"
                    gap="1rem"
                  >
                    <Button
                      variant="text"
                      onClick={handleBackClick}
                      data-testid={payerFormTestId('cancel')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleSubmitClick}
                      data-testid={payerFormTestId('save')}
                      disabled={isSaving || isSaved}
                      endIcon={
                        isSaving ? (
                          <CircularProgress
                            size={20}
                            color="inherit"
                          />
                        ) : null
                      }
                    >
                      Save
                    </Button>
                  </Stack>
                </form>
              </Stack>
            </Card>
          </Stack>
        </Container>
      </Box>

      <WarningModal
        open={isWarningModalOpen}
        onClose={() => {
          setIsWarningModalOpen(false);
          setForcePayerEnd(false);
          setForceCreatePayerAndEndPreviousPayer(false);
        }}
        title={payerWarningInfo.title}
        message={payerWarningInfo.message}
        buttonText={payerWarningInfo.confirmBtn}
        cancelBtn={payerWarningInfo.cancelBtn}
        handleSaveBtn={resubmitEditPayerHandler}
      />

      <WarningModal
        open={isWarningForAlertsModalOpen}
        onClose={() => setIsWarningForAlertsModalOpen(false)}
        title="Change Payer for Closed Month"
        message={`You are editing the payer for a closed-out month.
        UB04 and SLM reports will be updated automatically by tomorrow.
        Supervisor approval is required to proceed.`}
        buttonText="Confirm Change"
        cancelBtn="Cancel"
        handleSaveBtn={handleStartDateWarningConfirm}
      />

      <WarningModal
        open={isUnlockConfirmationOpen}
        onClose={() => setIsUnlockConfirmationOpen(false)}
        title="Prevent Gaps in Coverage and Billing"
        message="Setting a manual end date may create an uncovered period in the patient's certification or billing. To avoid issues, ensure the next payer starts immediately after this one ends."
        buttonText="Set End Date"
        cancelBtn="Cancel"
        handleSaveBtn={handleUnlockConfirmation}
      />

      {/* ARD payer overlap warning on payer edit */}
      <WarningModal
        open={isMdsPayerWarningOpen}
        onClose={() => setIsMdsPayerWarningOpen(false)}
        title="Payer Period Warning"
        message={mdsPayerWarningMessage}
        buttonText="Save Anyway"
        cancelBtn="Go Back"
        handleSaveBtn={() => {
          setIsMdsPayerWarningOpen(false);
          formik.handleSubmit();
        }}
      />

      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </>
  );
};

export default AddEditPatientPayer;
